import { PublicAxiosInstance } from '@/api/axios'
import { logoImages, utilsImages } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PageLoader } from '@/components/ui/loader'
import { endPoint } from '@/constant/end-point'
import { useTheme } from '@/hooks/use-theme'
import { useUnsavedChangesWarning } from '@/hooks/use-unsaved-changes-warning'
import { cn } from '@/lib/utils'
import { signupSchema, type SignupSchemaType } from '@/schema/auth/signup'
import { scrollToTop } from '@/utils/scroll-to-top'
import { useAuth, useSignUp } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

export function SignupForm({ className, ...props }: React.ComponentProps<'div'>) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const { isLoaded } = useSignUp()
  const { isSignedIn } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SignupSchemaType>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      phone: '',
    },
  })

  useUnsavedChangesWarning({ when: isDirty && !isSubmitting })
  const onSubmit = async (data: SignupSchemaType) => {
    try {
      await PublicAxiosInstance.post(endPoint.signupRequest, {
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        email: data.email,
        phone: data.phone,
      })
      toast.success('Signup request sent successfully', {
        description: 'Your signup request has been sent to the administrator for review',
      })
      navigate('/sign-in', { replace: true })
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 429) {
        toast.error('Too many requests!', {
          description: 'You have made too many requests. Please try again later.',
        })
        return
      }
      toast.error('Signup failed!', {
        description:
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Please try again',
      })
    }
  }

  useEffect(() => {
    // if user is signed in, navigate to home page
    if (isSignedIn) navigate('/', { replace: true })
  }, [isSignedIn, navigate])

  if (!isLoaded) return <PageLoader />

  return (
    <div className={cn('flex  flex-col gap-6 xl:w-[60%] lg:w-[80%] md:w-full w-full', className)} {...props}>
      <Card className='overflow-hidden p-0 shadow-md'>
        <CardContent className='grid p-0 md:grid-cols-2'>
          <form
            onSubmit={handleSubmit(onSubmit, () => {
              scrollToTop()
            })}
            className='p-6 md:p-8'
          >
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col items-center text-center'>
                <img
                  loading='lazy'
                  decoding='async'
                  src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                  alt='Image'
                  className='h-12 w-auto'
                />
                <h1 className='text-2xl font-medium pt-3'>Create your account</h1>
                <p className='text-muted-foreground text-sm'>Welcome! Please fill in the details to get started.</p>
              </div>
              <div className='grid md:grid-cols-2 grid-cols-1 gap-3'>
                <Input
                  label='First Name'
                  id='firstName'
                  type='text'
                  placeholder='Enter your first name (eg, John)'
                  required
                  {...register('firstName')}
                  error={errors.firstName?.message}
                />
                <Input
                  label='Last Name'
                  id='lastName'
                  type='text'
                  placeholder='Enter your last name (eg, Doe)'
                  required
                  {...register('lastName')}
                  error={errors.lastName?.message}
                />
              </div>
              <div className='grid md:grid-cols-2 grid-cols-1 gap-3'>
                <Input
                  label='Email'
                  id='email'
                  type='email'
                  placeholder='Enter your email (eg, john@example.com)'
                  required
                  {...register('email')}
                  error={errors.email?.message}
                />
                <Input
                  label='Phone'
                  id='phone'
                  type='tel'
                  placeholder='Enter your phone number (eg, +1234567890)'
                  required
                  {...register('phone')}
                  error={errors.phone?.message}
                />
              </div>
              <div className='grid gap-3'>
                <Input
                  label='Company Name'
                  required
                  id='companyName'
                  type='text'
                  placeholder='Enter your company name (eg, CISO Adapt)'
                  {...register('companyName')}
                  error={errors.companyName?.message}
                />
              </div>
              <Button type='submit' className='w-full' disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    <span>Signing up...</span>
                  </>
                ) : (
                  'Sign Up'
                )}
              </Button>
              <div className='text-center text-sm'>
                Already have an account?&nbsp;
                <Link to='/sign-in' className='underline underline-offset-4'>
                  Sign in
                </Link>
              </div>
            </div>
          </form>
          <div className='relative hidden md:block'>
            <img
              loading='lazy'
              decoding='async'
              src={utilsImages.loginPageSide}
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover   dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <Link to='/terms-of-service'>Terms of Service</Link> and&nbsp;
        <Link to='/privacy-policy'>Privacy Policy</Link>.
      </div>
    </div>
  )
}
